// ══════════════════════════════════════════════════════════════════════════════
// FİLO BİLDİRİM SESİ — TEK KAYNAK
//
// Filoya gelen HER bildirim duyulur olmalı: yeni sipariş, atanamama, katılım talebi,
// hakediş/fatura, acil durum. Önceden yalnız "kritik paket sayısı arttı" durumunda ses
// çalıyordu; geri kalan her şey sessizdi.
//
// Neden Web Audio (mp3 asset değil):
//   • asset yükleme/önbellek derdi yok, ilk bildirimde gecikme olmaz
//   • ses tipleri desen olarak ayrışır (yükselen üçlü = yeni sipariş, alçalan ikili =
//     dikkat, hızlı üçlü = acil) — kullanıcı ekrana bakmadan olayı ayırt eder
//
// Tarayıcı otomatik-oynatma kilidi: AudioContext ilk KULLANICI ETKİLEŞİMİNDE açılır.
// Kilit açılmadan gelen bildirimde ses çalmaz (tarayıcı engeli, bizim hatamız değil) —
// bu yüzden ilk tıklama/dokunma/tuş vuruşunda context hazırlanır ve `resume()` denenir.
// PWA olarak kurulduğunda bu kısıt gevşer (bkz. utils/pwaInstall.js).
// ══════════════════════════════════════════════════════════════════════════════

const MUTE_KEY = 'fleet-sound-muted';

/** Aynı olay saniyede birkaç kez gelirse (yeniden atama turları) ses spam'i olmasın. */
const MIN_GAP_MS = 350;
const SAME_KIND_GAP_MS = 1200;

/** Desenler: [frekans(Hz), başlangıç gecikmesi(sn), süre(sn)] + tepe ses seviyesi. */
const PATTERNS = {
    /** Yeni sipariş — yükselen üçlü, en dikkat çekici (ama ürkütmeyen) */
    newOrder: { gain: 0.20, notes: [[660, 0, 0.14], [880, 0.14, 0.14], [1175, 0.28, 0.20]] },
    /** Acil durum — hızlı, ısrarlı üçlü */
    emergency: { gain: 0.26, notes: [[988, 0, 0.12], [988, 0.16, 0.12], [988, 0.32, 0.18]] },
    /** Dikkat (atanamadı, bakiye yetersiz, kurye bulunamadı) — alçalan ikili */
    critical: { gain: 0.20, notes: [[880, 0, 0.16], [660, 0.18, 0.18]] },
    /** Olumlu (hakediş ödendi, onaylandı, teslim edildi) — yükselen ikili, yumuşak */
    success: { gain: 0.15, notes: [[784, 0, 0.12], [1047, 0.12, 0.18]] },
    /** Genel bildirim — tek yumuşak nota */
    info: { gain: 0.13, notes: [[784, 0, 0.16]] },
};

let ctx = null;
let unlocked = false;
let lastPlayAt = 0;
const lastKindAt = new Map();
const subs = new Set();

const readMuted = () => {
    try { return localStorage.getItem(MUTE_KEY) === '1'; } catch { return false; }
};

let muted = readMuted();

const publish = () => {
    subs.forEach((cb) => { try { cb(muted); } catch { /* yut */ } });
};

function ensureContext() {
    if (ctx) return ctx;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    ctx = new Ctx();
    return ctx;
}

/** İlk kullanıcı etkileşiminde ses kilidini aç (otomatik-oynatma politikası). */
export function installFleetSoundUnlock() {
    if (typeof window === 'undefined' || unlocked) return;

    const unlock = () => {
        const c = ensureContext();
        if (!c) return;
        if (c.state === 'suspended') c.resume().catch(() => {});
        unlocked = true;
        window.removeEventListener('pointerdown', unlock);
        window.removeEventListener('keydown', unlock);
        window.removeEventListener('touchstart', unlock);
    };

    window.addEventListener('pointerdown', unlock, { once: false });
    window.addEventListener('keydown', unlock, { once: false });
    window.addEventListener('touchstart', unlock, { once: false });
}

export function isFleetSoundMuted() { return muted; }

export function setFleetSoundMuted(next) {
    muted = !!next;
    try { localStorage.setItem(MUTE_KEY, muted ? '1' : '0'); } catch { /* yut */ }
    publish();
}

export function toggleFleetSoundMuted() {
    setFleetSoundMuted(!muted);
    // Sesi açan kullanıcı duyduğunu doğrulasın.
    if (!muted) playFleetSound('info', { force: true });
    return muted;
}

export function subscribeFleetSoundMuted(cb) {
    subs.add(cb);
    cb(muted);
    return () => { subs.delete(cb); };
}

/**
 * Bildirim sesi çal.
 * @param {'newOrder'|'emergency'|'critical'|'success'|'info'} kind
 * @param {{force?: boolean}} [opts] force: hız sınırını atla (kullanıcı testi)
 */
export function playFleetSound(kind = 'info', opts = {}) {
    if (muted) return;

    const pattern = PATTERNS[kind] || PATTERNS.info;
    const now = Date.now();

    if (!opts.force) {
        if (now - lastPlayAt < MIN_GAP_MS) return;
        if (now - (lastKindAt.get(kind) || 0) < SAME_KIND_GAP_MS) return;
    }

    try {
        const c = ensureContext();
        if (!c) return;
        if (c.state === 'suspended') c.resume().catch(() => {});

        const t0 = c.currentTime;
        pattern.notes.forEach(([freq, delay, dur]) => {
            const osc = c.createOscillator();
            const gain = c.createGain();
            osc.type = 'sine';
            osc.frequency.value = freq;

            const start = t0 + delay;
            gain.gain.setValueAtTime(0.0001, start);
            gain.gain.exponentialRampToValueAtTime(pattern.gain, start + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);

            osc.connect(gain).connect(c.destination);
            osc.start(start);
            osc.stop(start + dur + 0.02);
        });

        lastPlayAt = now;
        lastKindAt.set(kind, now);
    } catch { /* ses çalınamadıysa akış durmaz */ }
}
